import React, { useEffect, CSSProperties } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { Toast } from './Toast';
import { removeNotification } from '@/store/slices/notificationsSlice';
import { cn } from '@/utils/classNames';

/**
 * ToastContainer is responsible for displaying and managing multiple toast notifications
 */
export const ToastContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notifications, config } = useAppSelector(state => state.notifications);

  const handleCloseToast = (id: string) => {
    dispatch(removeNotification(id));
  };

  // Positioning classes based on the config
  const getPositionClasses = () => {
    const positions = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
    };

    return positions[config.position];
  };

  // Sort notifications by creation time (newest first or oldest first)
  const getSortedNotifications = () => {
    // Make a copy to avoid modifying the original array
    const sortedNotifications = [...notifications];

    // Determine sorting order based on position
    const isTopPosition = config.position.startsWith('top-');

    if (isTopPosition) {
      // For top positions, show newest notifications on top
      return sortedNotifications
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, config.maxVisible);
    } else {
      // For bottom positions, show newest notifications on bottom
      return sortedNotifications
        .sort((a, b) => a.createdAt - b.createdAt)
        .slice(0, config.maxVisible);
    }
  };

  // Determine appropriate gap and stacking based on position
  const getContainerStyles = (): CSSProperties => {
    const isBottomPosition = config.position.includes('bottom-');

    return {
      flexDirection: isBottomPosition ? ('column-reverse' as const) : ('column' as const),
    };
  };

  return (
    <div
      className={cn('fixed z-50 flex gap-3 w-full sm:max-w-md', getPositionClasses())}
      style={getContainerStyles()}
      aria-live="polite"
    >
      {getSortedNotifications().map(notification => (
        <Toast key={notification.id} notification={notification} onClose={handleCloseToast} />
      ))}
    </div>
  );
};
